import React, { useEffect, useState } from "react";
import { Button, InputLabel, Popover, Stack } from "@mantine/core";
import styles from "./styles.module.css";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import "@mantine/dates/styles.css";
import { Calendar, TimePicker } from "@mantine/dates";
import Text from "@/components/atoms/Text";

interface Props {
  label?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  testId?: string;
  showTimePicker?: boolean;
  minDate?: Date;
  value?: Date | null;
  onChange?: (date: Date | null) => void;
}

const DatePicker: React.FC<Props> = ({
  label,
  disabled,
  required,
  error,
  testId,
  showTimePicker,
  minDate,
  value,
  onChange,
}) => {
  const { t } = useTranslation();

  const [time, setTime] = useState<string>(() => (value ? dayjs(value).format("HH:mm") : "00:00"));

  useEffect(() => {
    setTime(value ? dayjs(value).format("HH:mm") : "00:00");
  }, [value]);

  const formatPlaceholder = () => {
    if (!value) return t("actions:selectDate");
    return showTimePicker ? dayjs(value).format("DD.MM.YYYY HH:mm") : dayjs(value).format("DD.MM.YYYY");
  };

  const applyTime = (date: Date | string, timeStr: string): Date => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    return dayjs(date).hour(hours).minute(minutes).second(0).millisecond(0).toDate();
  };

  const nowTime = () => dayjs().format("HH:mm");
  const isSelectedDayToday = (date: Date | string) => dayjs(date).isSame(dayjs(), "day");

  const handleDayClick = (date: Date | string) => {
    let timeToUse = time;
    if (isSelectedDayToday(date) && time < nowTime()) {
      timeToUse = nowTime();
      setTime(timeToUse);
    }
    onChange?.(applyTime(date, timeToUse));
  };

  const handleTimeChange = (newTime: string) => {
    const isToday = value ? isSelectedDayToday(value) : false;
    const clamped = isToday && newTime < nowTime() ? nowTime() : newTime;
    setTime(clamped);
    if (value) {
      onChange?.(applyTime(value, clamped));
    }
  };

  return (
    <Stack gap={3.5}>
      {label && (
        <InputLabel
          data-testid={`${testId}-label`}
          required={required}
          style={{ color: "var(--color-text-secondary)", fontWeight: 300 }}
        >
          {label}
        </InputLabel>
      )}
      <Popover width={"md"} position={"bottom-start"} shadow={"md"} disabled={disabled}>
        <Popover.Target>
          <Button unstyled fullWidth={true} className={styles.button} disabled={disabled} data-testid={`${testId}-btn`}>
            {formatPlaceholder()}
            <SVGAsset name={"chevron-down-grey"} className={styles.icon} />
          </Button>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack>
            <Calendar
              minDate={minDate}
              getDayProps={(date) => ({
                selected: !!value && dayjs(value).isSame(dayjs(date), "day"),
                onClick: () => handleDayClick(date),
              })}
            />
            {showTimePicker && <TimePicker label={t("common:time")} value={time} onChange={handleTimeChange} />}
          </Stack>
        </Popover.Dropdown>
      </Popover>
      {error && (
        <Text color={"error"} size={"xs"} data-testid={`${testId}-error`}>
          {error}
        </Text>
      )}
    </Stack>
  );
};

export default DatePicker;
